// src/excel/builders/ads/buildWorkbook.js
// XLSX builder for the Ads service

export function buildAdsWorkbook(XLSX, params) {
  const {
    t,
    shortcode,
    stationName,
    serviceName,
    date,
    blocks = [],
  } = params || {};


  const wb = XLSX.utils.book_new();
  const ws = {};
  const merges = [];

  const brandText = (t && t('listeners.brand_line_excel')) || 'Radiomonitor.Net';
  const serviceTitle = serviceName || (t && t('ads.title')) || 'Advertising statistics';
  const stationTitle = stationName || shortcode || '';
  const dateText = `${(t && t('ads.date')) || 'Date'}: ${date || ''}`;

  let currentRow = 0;

  // Brand row
  XLSX.utils.sheet_add_aoa(ws, [[brandText]], { origin: { r: currentRow, c: 0 } });
  merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 3 } });
  const brandAddr = XLSX.utils.encode_cell({ r: currentRow, c: 0 });
  ws[brandAddr] = ws[brandAddr] || {};
  ws[brandAddr].s = {
    font: { bold: true, sz: 20, color: { rgb: '1F2A5A' } },
    fill: { fgColor: { rgb: 'EEF2FF' } },
    alignment: { horizontal: 'left', vertical: 'center' }
  };
  currentRow += 2;

  const infoRows = [
    [serviceTitle],
    ...(stationTitle ? [[stationTitle]] : []),
    [dateText],
    ['']
  ];

  XLSX.utils.sheet_add_aoa(ws, infoRows, { origin: { r: currentRow, c: 0 } });
  for (let i = 0; i < infoRows.length; i += 1) {
    merges.push({ s: { r: currentRow + i, c: 0 }, e: { r: currentRow + i, c: 3 } });
  }

  const encode = XLSX.utils.encode_cell;
  const serviceAddr = encode({ r: currentRow, c: 0 });
  if (ws[serviceAddr]) {
    ws[serviceAddr].s = {
      font: { bold: true, sz: 18, color: { rgb: '1F1F1F' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
  }
  if (stationTitle) {
    const stationAddr = encode({ r: currentRow + 1, c: 0 });
    if (ws[stationAddr]) {
      ws[stationAddr].s = {
        font: { bold: true, sz: 16, color: { rgb: '1F1F1F' } },
        alignment: { horizontal: 'left', vertical: 'center' }
      };
    }
  }

  currentRow += infoRows.length;

  const HEAD_START = (t && t('ads.table.start')) || 'Start';
  const HEAD_END = (t && t('ads.table.end')) || 'End';
  const HEAD_CLIPS = (t && t('ads.table.clips')) || 'Clips';
  const HEAD_LISTENERS = (t && t('ads.table.listeners')) || 'Listeners';

  const headerRow = [HEAD_START, HEAD_END, HEAD_CLIPS, HEAD_LISTENERS];
  const tableRows = (blocks || []).map((block) => {
    const listeners = Number(block.listeners || 0);
    const clipsText = Array.isArray(block.clips)
      ? block.clips.join('\n')
      : String(block.clips || '');
    return [
      block.start?.slice(0, 8) || '',
      block.end?.slice(0, 8) || '',
      clipsText,
      Number.isFinite(listeners) ? listeners : 0
    ];
  });

  const table = [
    headerRow,
    ...tableRows
  ];

  XLSX.utils.sheet_add_aoa(ws, table, { origin: { r: currentRow, c: 0 } });

  const headerRowIndex = currentRow;
  const totalRowIndex = currentRow + table.length - 1;

  const headerStyle = {
    font: { bold: true, color: { rgb: '333333' } },
    fill: { fgColor: { rgb: 'F0F0F0' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: 'D0D0D0' } },
      bottom: { style: 'thin', color: { rgb: 'D0D0D0' } },
      left: { style: 'thin', color: { rgb: 'D0D0D0' } },
      right: { style: 'thin', color: { rgb: 'D0D0D0' } }
    }
  };

  const borderStyle = {
    border: {
      top: { style: 'thin', color: { rgb: 'E0E0E0' } },
      bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
      left: { style: 'thin', color: { rgb: 'E0E0E0' } },
      right: { style: 'thin', color: { rgb: 'E0E0E0' } }
    }
  };

  for (let c = 0; c < headerRow.length; c += 1) {
    const addr = encode({ r: headerRowIndex, c });
    if (ws[addr]) ws[addr].s = headerStyle;
  }

  for (let r = headerRowIndex + 1; r <= totalRowIndex; r += 1) {
    for (let c = 0; c < headerRow.length; c += 1) {
      const addr = encode({ r, c });
      if (!ws[addr]) continue;
      ws[addr].s = {
        ...(ws[addr].s || {}),
        ...borderStyle,
        alignment: {
          horizontal: c === 2 ? 'left' : c === 3 ? 'right' : 'center',
          vertical: 'center',
          wrapText: c === 2
        },
        font: { sz: 11, color: { rgb: '1F1F1F' } }
      };
      if (c === 3 && typeof ws[addr].v === 'number') {
        ws[addr].t = 'n';
        ws[addr].z = '#,##0';
      }
    }
  }

  ws['!cols'] = [
    { wch: 12 },
    { wch: 12 },
    { wch: 40 },
    { wch: 14 }
  ];

  ws['!merges'] = merges;

  if (table.length > 1) {
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: totalRowIndex, c: headerRow.length - 1 }
      })
    };
  }

  try {
    ws['!freeze'] = { xSplit: 0, ySplit: headerRowIndex + 1 };
  } catch {
    // ignore freeze errors
  }

  XLSX.utils.book_append_sheet(wb, ws, 'Ads');
  return wb;
}

export default buildAdsWorkbook;
